import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { CircleCheck, Clock, TriangleAlert, Users } from 'lucide-react';
import { useGetPickupBoardQuery, safepassApi, getApiError } from '@/store/api';
import {
  getSocket,
  joinInstitutionRoom,
  leaveInstitutionRoom,
  useSocketStatus,
  type PickupEventPayload,
} from '@/lib/socket';
import { useAuthStore } from '@/store/auth.store';
import { PageLayout } from '@/components/ui/PageLayout';
import { StatTile } from '@/components/ui/StatTile';
import { Mark } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { PageLoader } from '@/components/ui/Spinner';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';

interface FeedItem {
  id: string;
  studentName: string;
  collectorName: string;
  type: string;
  timestamp: string;
}

const timeFmt = new Intl.DateTimeFormat('en-GH', { hour: '2-digit', minute: '2-digit' });
const dateFmt = new Intl.DateTimeFormat('en-GH', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

/*
  ─────────────────────────────────────────────────────────────────────────────
  The live dismissal board.

  This is the screen that is on a wall or a spare monitor for the twenty minutes
  that matter, so it is built to be read from further away than the rest of the
  product: bigger figures, and colour used only where it carries meaning.

  WHAT CHANGED

  · The local `StatCard`, whose `colour` prop took a raw Tailwind string, is now
    the shared StatTile with a semantic tone. Flagged is the only tile painted
    red, because a board where all four tiles are coloured spends the reader's
    attention evenly and so tells them nothing.

  · Progress is one bar with the figure beside it, not a card of its own with a
    heading, a percentage, a bar and a caption saying the same thing twice.

  · The connection state moved to the command bar, where it is visible from
    every screen rather than only this one.
  ─────────────────────────────────────────────────────────────────────────────
*/
export function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const institutionId = user?.institutionId ?? '';
  const dispatch = useDispatch();
  const connected = useSocketStatus();
  const [feed, setFeed] = useState<FeedItem[]>([]);
  const feedRef = useRef<HTMLDivElement>(null);

  const { data: board, isLoading, isError, error } = useGetPickupBoardQuery(institutionId, {
    skip: !institutionId,
    pollingInterval: 30_000,
  });

  useEffect(() => {
    if (!institutionId) return;
    const socket = getSocket();

    const onPickup = (payload: PickupEventPayload) => {
      setFeed((prev) => [{ id: `${Date.now()}-${prev.length}`, ...payload }, ...prev.slice(0, 49)]);
      dispatch(safepassApi.util.invalidateTags(['PickupBoard']));
    };

    socket.on('pickup_event', onPickup);
    joinInstitutionRoom(institutionId);

    return () => {
      socket.off('pickup_event', onPickup);
      leaveInstitutionRoom(institutionId);
    };
  }, [institutionId, dispatch]);

  useEffect(() => {
    feedRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [feed.length]);

  const total = board?.total ?? 0;
  const released = board?.released ?? 0;
  const pending = board?.pending ?? 0;
  const flagged = board?.flagged ?? 0;
  const pct = total > 0 ? Math.round((released / total) * 100) : 0;

  return (
    <PageLayout
      title="Live board"
      subtitle={dateFmt.format(new Date())}
      contained={false}
      meta={
        <>
          <Mark tone={connected ? 'success' : 'warning'} dot={connected}>
            {connected ? 'Feed live' : 'Feed offline'}
          </Mark>
          {board?.date && <Mark tone="neutral">Dismissal {board.date}</Mark>}
        </>
      }
    >
      {isLoading ? (
        <div className={styles.panel}>
          <PageLoader label="Loading the board…" />
        </div>
      ) : isError ? (
        <div className={styles.panel}>
          <EmptyState
            icon={<TriangleAlert size={17} />}
            title="Could not load the board"
            description={getApiError(error)}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {/* ── Figures ──────────────────────────────────────────────────── */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <StatTile
              label="Enrolled today"
              value={total}
              icon={<Users size={17} />}
              tone="neutral"
            />
            <StatTile
              label="Released"
              value={released}
              icon={<CircleCheck size={17} />}
              tone="success"
              hint={total > 0 ? `${pct}% of ${total}` : undefined}
            />
            <StatTile
              label="Still waiting"
              value={pending}
              icon={<Clock size={17} />}
              tone={pending > 0 ? 'warning' : 'neutral'}
            />
            <StatTile
              label="Flagged"
              value={flagged}
              icon={<TriangleAlert size={17} />}
              tone={flagged > 0 ? 'danger' : 'neutral'}
              hint={flagged > 0 ? 'Needs review' : 'None today'}
            />
          </div>

          {/* ── Progress ─────────────────────────────────────────────────── */}
          {total > 0 && (
            <div className={cn(styles.panel, 'px-4 py-3')}>
              <div className="flex items-baseline justify-between gap-3">
                <p className={styles.label}>Dismissal progress</p>
                <p className="text-[12px] font-bold tabular-nums text-foreground">
                  {released}
                  <span className="text-muted-foreground"> / {total}</span>
                </p>
              </div>
              {/* Square, like everything else. A rounded-full bar was the one
                  pill left in the product. */}
              <div
                className="mt-2 h-1.5 overflow-hidden rounded-sm bg-muted"
                role="progressbar"
                aria-valuenow={pct}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label="Children collected"
              >
                <div
                  className="h-full bg-success transition-[width] duration-700 ease-out"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          )}

          {/* ── Live feed ────────────────────────────────────────────────── */}
          <div className={cn(styles.panel, 'overflow-clip')}>
            <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
              <p className={styles.sectionTitle}>Gate activity</p>
              {feed.length > 0 && (
                <p className="text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground tabular-nums">
                  {feed.length} this session
                </p>
              )}
            </div>

            <div ref={feedRef} className="max-h-[420px] overflow-y-auto">
              {feed.length === 0 ? (
                <EmptyState
                  icon={<Clock size={17} />}
                  title={connected ? 'Waiting for the first pickup' : 'Not receiving events'}
                  description={
                    connected
                      ? 'Releases appear here the moment a gate officer verifies a code.'
                      : 'The live feed is disconnected, so nothing will appear here. The figures above are still refreshed every 30 seconds.'
                  }
                />
              ) : (
                feed.map((item) => {
                  const ok = item.type === 'released';
                  return (
                    <div
                      key={item.id}
                      className="flex items-center gap-3 border-b border-border px-4 py-2.5 transition-colors last:border-b-0 hover:bg-accent/60 animate-fade-in"
                    >
                      <span
                        className={cn(
                          'flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md border text-[11px] font-bold',
                          ok
                            ? 'border-success/25 bg-success/[0.09] text-[hsl(var(--record-ink-success))]'
                            : 'border-destructive/25 bg-destructive/[0.07] text-[hsl(var(--record-ink-danger))]',
                        )}
                        aria-hidden
                      >
                        {item.studentName?.[0]?.toUpperCase() ?? '?'}
                      </span>

                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[12.5px] font-semibold text-foreground">
                          {item.studentName}
                        </p>
                        <p className="truncate text-[11px] text-muted-foreground">
                          {ok ? `Released to ${item.collectorName}` : 'Verification failed at the gate'}
                        </p>
                      </div>

                      <div className="flex flex-shrink-0 items-center gap-2.5">
                        <Mark tone={ok ? 'success' : 'danger'}>{ok ? 'Released' : 'Failed'}</Mark>
                        <span className="text-[11px] tabular-nums text-muted-foreground">
                          {timeFmt.format(new Date(item.timestamp))}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
}
